import { useEffect, useRef, useState } from "react";
import { X, Phone, ShieldCheck, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuthStore, useUIStore } from "@/lib/stores";
import toast from "react-hot-toast";

export default function OTPLoginModal() {
    const { loginOpen, setLoginOpen } = useUIStore();
    const { login } = useAuthStore();
    const [step, setStep] = useState("phone");
    const [phone, setPhone] = useState("");
    const [name, setName] = useState("");
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [reqId, setReqId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [debugOtp, setDebugOtp] = useState(null);
    const [cooldown, setCooldown] = useState(0);
    const otpRefs = useRef([]);

    useEffect(() => {
        if (!loginOpen) {
            setStep("phone"); setPhone(""); setName(""); setOtp(["","","","","",""]);
            setReqId(null); setErr(""); setDebugOtp(null);
        }
    }, [loginOpen]);

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const sendOTP = async () => {
        setErr("");
        if (phone.length !== 10 || !/^[6-9]\d{9}$/.test(phone)) {
            setErr("Please enter a valid 10-digit Indian mobile number.");
            return;
        }
        setLoading(true);
        try {
            const r = await api.post("/auth/send-otp", { phone });
            setReqId(r.data.request_id);
            setDebugOtp(r.data.debug_otp);
            setStep("otp");
            setCooldown(30);
            toast.success("OTP sent to your phone");
        } catch (e) {
            setErr(e.response?.data?.detail || "Failed to send OTP");
        } finally {
            setLoading(false);
        }
    };

    const verifyOTP = async () => {
        setErr("");
        const code = otp.join("");
        if (code.length !== 6) { setErr("Please enter the 6-digit OTP."); return; }
        setLoading(true);
        try {
            const r = await api.post("/auth/verify-otp", {
                request_id: reqId,
                otp: code,
                name: name || undefined,
            });
            login(r.data.token, r.data.user);
            toast.success("Welcome to LoyltyKart.");
            setLoginOpen(false);
        } catch (e) {
            setErr(e.response?.data?.detail || "Verification failed");
            setOtp(["", "", "", "", "", ""]);
            otpRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const onOtpChange = (i, v) => {
        const digit = v.replace(/\D/g, "").slice(-1);
        const next = [...otp];
        next[i] = digit;
        setOtp(next);
        if (digit && i < 5) otpRefs.current[i + 1]?.focus();
    };

    const onOtpKey = (i, e) => {
        if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
        if (e.key === "Enter") verifyOTP();
    };

    if (!loginOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-rk-navy/80 backdrop-blur-sm px-0 md:px-4"
            onClick={() => setLoginOpen(false)}
            data-testid="otp-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative bg-white w-full md:max-w-md p-8 md:p-12 shadow-2xl rk-fade-up border-t-4 border-rk-gold"
            >
                <button data-testid="close-login-btn" onClick={() => setLoginOpen(false)} className="absolute top-4 right-4 p-2 hover:bg-rk-cream">
                    <X className="w-5 h-5 text-rk-navy" />
                </button>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-rk-gold-deep mb-4">
                    <ShieldCheck className="w-3 h-3" /> Secure Sign-in
                </div>
                <h2 className="font-serif text-4xl text-rk-navy font-light">
                    {step === "phone" ? <>Welcome to<br /><em className="italic text-rk-gold">LoyltyKart</em></> : "Enter the passcode"}
                </h2>
                <div className="rk-divider-gold w-16 mt-4 mb-6" />
                <p className="text-sm text-rk-muted mb-8">
                    {step === "phone"
                        ? "A 6-digit one-time code will be sent to your mobile. No password, no friction."
                        : <>Sent to <span className="text-rk-navy font-semibold">+91 {phone.slice(0, 5)} {phone.slice(5)}</span></>}
                </p>

                {step === "phone" ? (
                    <div className="space-y-5">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.28em] text-rk-muted">Mobile number</label>
                            <div className="mt-2 flex items-stretch border border-rk-navy/20 focus-within:border-rk-gold focus-within:ring-1 focus-within:ring-rk-gold">
                                <div className="flex items-center gap-1.5 px-4 bg-rk-navy text-rk-gold text-sm font-semibold">
                                    <Phone className="w-3.5 h-3.5" /> +91
                                </div>
                                <input
                                    data-testid="phone-input"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                                    placeholder="98765 43210"
                                    autoFocus
                                    className="flex-1 px-4 py-4 text-base outline-none bg-transparent text-rk-navy"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.28em] text-rk-muted">Your name (optional)</label>
                            <input
                                data-testid="name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Priya Sharma"
                                className="mt-2 w-full px-4 py-4 border border-rk-navy/20 text-sm outline-none focus:border-rk-gold focus:ring-1 focus:ring-rk-gold text-rk-navy"
                            />
                        </div>
                        {err && <p className="text-sm text-red-600">{err}</p>}
                        <button
                            data-testid="send-otp-btn"
                            onClick={sendOTP}
                            disabled={loading}
                            className="rk-btn-primary w-full inline-flex items-center justify-center gap-2"
                        >
                            {loading ? "Sending..." : "Send OTP"}
                            <ArrowRight className="w-4 h-4" />
                        </button>
                        <p className="text-[10px] text-center uppercase tracking-[0.22em] text-rk-muted">
                            By continuing, you agree to our Terms & Privacy Policy.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-5">
                        {debugOtp && window.location.hostname.includes("preview") && (
                            <div className="rk-foil px-4 py-3 text-sm text-rk-navy">
                                <strong className="uppercase tracking-wider text-[10px] text-rk-gold-deep">Preview environment</strong><br />
                                Use code <code className="font-mono font-bold text-lg">{debugOtp}</code>
                            </div>
                        )}
                        <div className={`grid grid-cols-6 gap-2 ${err ? "rk-shake" : ""}`}>
                            {otp.map((v, i) => (
                                <input
                                    data-testid={`otp-input-${i}`}
                                    key={i}
                                    ref={(el) => (otpRefs.current[i] = el)}
                                    value={v}
                                    onChange={(e) => onOtpChange(i, e.target.value)}
                                    onKeyDown={(e) => onOtpKey(i, e)}
                                    inputMode="numeric"
                                    maxLength={1}
                                    className="aspect-square text-center font-serif text-3xl font-semibold border border-rk-navy/20 focus:border-rk-gold focus:ring-1 focus:ring-rk-gold outline-none text-rk-navy bg-white"
                                />
                            ))}
                        </div>
                        {err && <p className="text-sm text-red-600 text-center">{err}</p>}
                        <button
                            data-testid="verify-otp-btn"
                            onClick={verifyOTP}
                            disabled={loading}
                            className="rk-btn-primary w-full"
                        >
                            {loading ? "Verifying..." : "Verify & Continue"}
                        </button>
                        <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em]">
                            <button
                                data-testid="change-phone-btn"
                                onClick={() => setStep("phone")}
                                className="text-rk-muted hover:text-rk-navy rk-link-gold"
                            >
                                Change number
                            </button>
                            <button
                                data-testid="resend-otp-btn"
                                disabled={cooldown > 0}
                                onClick={sendOTP}
                                className="text-rk-navy font-semibold rk-link-gold disabled:text-rk-muted"
                            >
                                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
